import { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { computed } from 'vue';

import PartnersInReportCell from '@/components/reports/PartnersInReportCell.vue';
import ReportAccessCell from '@/components/reports/ReportAccessCell.vue';
import ReportNameAndFolderCell from '@/components/reports/ReportNameAndFolderCell.vue';
import ReportNotificationsCell from '@/components/reports/ReportNotificationsCell.vue';
import ReportOverflowMenuCell from '@/components/reports/ReportOverflowMenuCell.vue';
import ReportPartnerOverlapsCell from '@/components/reports/ReportPartnerOverlapsCell.vue';
import ReportTypeCell from '@/components/reports/ReportTypeCell.vue';
import ReportUserCell from '@/components/reports/ReportUserCell.vue';

import { FEATURE_FLAG } from '@/constants/feature_flags';
import { ReportCell } from '@/interfaces/reports';
import { useFeatureFlagStore } from '@/stores';

export function useReportsColumns() {
  const nameComparator = (cellA: ReportCell, cellB: ReportCell) => {
    if (cellA.report.name === cellB.report.name) return 0;
    return cellA.report.name > cellB.report.name ? 1 : -1;
  };

  const partnerComparator = (cellA: ReportCell, cellB: ReportCell) => {
    if (cellA.partnersInReport.length === cellB.partnersInReport.length)
      return 0;
    return cellA.partnersInReport.length > cellB.partnersInReport.length
      ? 1
      : -1;
  };

  const timeComparator = (cellA: ReportCell, cellB: ReportCell) => {
    if (cellA.report.updated_at === cellB.report.updated_at) return 0;
    return cellA.report.updated_at > cellB.report.updated_at ? 1 : -1;
  };

  const legacyColumns = computed<ColDef[]>(() => {
    return [
      {
        cellRenderer: ReportNameAndFolderCell,
        field: 'reportName',
        valueGetter: (params: ICellRendererParams<ReportCell>) => {
          return params.data?.reportName;
        },
        getQuickFilterText: (params: ICellRendererParams<ReportCell>) => {
          if (!params?.value?.report) return '';
          return params.value.report.name;
        },
        sortable: true,
        initialSort: 'asc',
        comparator: (cellA: ReportCell, cellB: ReportCell) =>
          nameComparator(cellA, cellB),
        minWidth: 300,
      },
      {
        cellRenderer: PartnersInReportCell,
        field: 'partnersInReport',
        sortable: true,
        comparator: (cellA: ReportCell, cellB: ReportCell) =>
          partnerComparator(cellA, cellB),
        maxWidth: 200,
      },
      {
        cellRenderer: ReportTypeCell,
        field: 'reportType',
        maxWidth: 150,
      },
      {
        cellRenderer: ReportUserCell,
        field: 'lastEdited',
        sortable: true,
        comparator: (cellA: ReportCell, cellB: ReportCell) =>
          timeComparator(cellA, cellB),
        maxWidth: 200,
      },
      {
        cellRenderer: ReportNotificationsCell,
        field: 'notifications',
        maxWidth: 120,
      },
      {
        cellRenderer: ReportOverflowMenuCell,
        field: ' ',
        maxWidth: 62,
      },
    ] as ColDef[];
  });

  const combinedColumns = computed<ColDef[]>(() => {
    return [
      {
        cellRenderer: ReportNameAndFolderCell,
        field: 'reportName',
        valueGetter: (params: ICellRendererParams<ReportCell>) => {
          return params.data?.reportName;
        },
        getQuickFilterText: (params: ICellRendererParams<ReportCell>) => {
          if (!params?.value?.report) return '';
          return params.value.report.name;
        },
        sortable: true,
        initialSort: 'asc',
        comparator: (cellA: ReportCell, cellB: ReportCell) =>
          nameComparator(cellA, cellB),
        minWidth: 300,
      },
      {
        cellRenderer: ReportPartnerOverlapsCell,
        field: 'partnerOverlaps',
        maxWidth: 200,
      },
      {
        cellRenderer: ReportAccessCell,
        field: 'access',
        maxWidth: 200,
      },
      {
        cellRenderer: ReportUserCell,
        field: 'owner',
        maxWidth: 200,
        sortable: true,
        comparator: (cellA: ReportCell, cellB: ReportCell) =>
          timeComparator(cellA, cellB),
      },
      {
        cellRenderer: ReportUserCell,
        field: 'lastEdited',
        sortable: true,
        comparator: (cellA: ReportCell, cellB: ReportCell) =>
          timeComparator(cellA, cellB),
        maxWidth: 200,
      },
      {
        cellRenderer: ReportOverflowMenuCell,
        field: ' ',
        maxWidth: 62,
      },
    ] as ColDef[];
  });

  const { hasFeatureFlag } = useFeatureFlagStore();
  const hasCombinedCreateAndSavedReports = computed(() => {
    return hasFeatureFlag(FEATURE_FLAG.TEMP_CREATE_AND_SAVED_REPORTS);
  });

  const columns = computed<ColDef[]>(() => {
    if (hasCombinedCreateAndSavedReports.value) {
      return combinedColumns.value;
    }
    return legacyColumns.value;
  });

  return {
    columns,
    hasCombinedCreateAndSavedReports,
  };
}
